import { postAPI } from '@/services-ts/axios'
import type { ICommunity, ICommunityMember } from '@/types/Data'
import type {
  ICreateCommunity,
  IDeleteCommunityMember,
  IGetCommunityAdmin,
  IGetCommunityBySearch,
  IGetCommunityMemberList,
  IInsertCommunityMember,
  IUpdateCommunity,
} from '@/types/Request/PostAPI'
import type { ICommunityStore } from '@/types/Store/State'
import { Alert, Format } from '@/utils-ts'
import { defineStore } from 'pinia'

export const useCommunityStore = defineStore('community', {
  state: (): ICommunityStore => ({
    communityList: {
      data: [],
      total: 0,
    },
    memberList: {
      data: [],
      total: 0,
    },
  }),

  actions: {
    async GetCommunityList(payload: IGetCommunityAdmin) {
      const { status, total, data } = await postAPI.CommunityServices.GetCommunityList(payload)
      this.communityList = status ? { total: total || 0, data } : { data: [], total: 0 }
    },

    async GetCommunityById(Id: string) {
      const payload = {
        Id,
        Skip: 0,
        Take: 1,
      }
      const { status, data } = await postAPI.CommunityServices.GetCommunityList(payload)
      return status && data.length > 0 ? data[0] : null
    },

    async CreateCommunity(payload: ICreateCommunity) {
      const { status, data, message } = await postAPI.CommunityServices.CreateCommunity(payload)

      if (status) {
        const item = await this.GetCommunityById(data)
        if (item) {
          this.communityList.data.push(item)
          this.communityList.data.sort((a, b) => a.name.localeCompare(b.name))
          this.communityList.total += 1
        }

        void Alert({
          message: message || Format('admin_global_success'),
          type: 'success',
        })
      }

      return status
    },

    async UpdateCommunity(payload: IUpdateCommunity): Promise<boolean> {
      if (!payload.Id) return false

      const { status, message } = await postAPI.CommunityServices.UpdateCommunity(payload)

      if (status) {
        const item = await this.GetCommunityById(payload.Id)
        const index = this.communityList.data.findIndex((x: ICommunity) => x.id === payload.Id)
        if (index !== -1 && item) this.communityList.data[index] = item
        this.communityList.data.sort((a, b) => a.name.localeCompare(b.name))

        void Alert({ message: message || Format('admin_global_updated'), type: 'success' })
      }

      return status
    },

    async DeleteCommunity(Id: string) {
      const { status, message } = await postAPI.CommunityServices.DeleteCommunity(Id)

      if (status) {
        this.communityList.data = this.communityList.data.filter(x => x.id !== Id)

        void Alert({ message: message || Format('admin_global_deleted'), type: 'success' })
      }
    },

    async GetCommunityMemberList(payload: IGetCommunityMemberList) {
      const { status, data, total } =
        await postAPI.CommunityServices.GetCommunityMemberList(payload)
      this.memberList = status ? { data, total: total || 0 } : { data: [], total: 0 }
    },

    async InsertCommunityMember(data: IInsertCommunityMember) {
      const { status, message } = await postAPI.CommunityServices.InsertCommunityMember(data)

      if (status) {
        void Alert({ message: message || Format('admin_global_success'), type: 'success' })
      }
    },

    async DeleteCommunityMember(payload: IDeleteCommunityMember) {
      const { status, message } = await postAPI.CommunityServices.DeleteCommunityMember(payload)
      if (status) {
        this.memberList.data = this.memberList.data.filter(
          (x: ICommunityMember) => x.userId !== payload.CommunityMemberUserId,
        )
        void Alert({ message: message || Format('admin_global_deleted'), type: 'success' })
      }
    },

    async GetComunityBySearch(payload: IGetCommunityBySearch) {
      const { status, data, total } = await postAPI.CommunityServices.GetCommunityBySearch(payload)
      this.communityList = status ? { data, total: total || 0 } : { data: [], total: 0 }
    },
  },
})
